// src/utils/estimateProfit.js — doced-8f3a61c2 (Document editor, Stage 5g: the Profit card)
//
// THE profit rule for an estimate, shared by the web Profit card (DocumentProfitCard.js) and — as a checked
// copy — the phone builder's Profit block (spatchy-mobile src/utils/estimateProfit.js; its test lifts THIS file
// and compares). Self-contained on purpose: no imports, so the phone test can lift it.
//
// HARD RULE (Marcus, 2026-10-30): cost, profit and margin are computed ON SCREEN ONLY, for Owner / Admin /
// Manager. Nothing here is ever written to the estimate, its lines, or anything a customer can reach.
//
// Founder decisions (2026-10-30):
//   • the lines counted are the SAME lines as the Total (add-ons included);
//   • an Included line counts its cost (its price is 0);
//   • cost = today's catalog cost (getCatalogCosts), not the cost when the estimate was written; before tax.
//
// A line has a cost when it is linked to the catalog (serviceId) AND the catalog answered a number for it.
// Every other line with something on it is "no cost, not counted". Blank lines are ignored.

const toCents = (n) => Math.round((Number(n) || 0) * 100);

/** The catalog ids the card asks costs for: unique, non-empty, sorted (a stable key). */
export function profitServiceIds(lines) {
    const ids = new Set();
    for (const l of Array.isArray(lines) ? lines : []) {
        const id = l && typeof l.serviceId === 'string' ? l.serviceId.trim() : '';
        if (id) ids.add(id);
    }
    return Array.from(ids).sort();
}

function isBlankLine(l) {
    if (!l || typeof l !== 'object') return true;
    const name = typeof l.name === 'string' ? l.name.trim() : '';
    return !name && !(Number(l.price) || 0) && !(typeof l.serviceId === 'string' && l.serviceId.trim());
}

/**
 * lines       the estimate's lineItems (as on screen)
 * totalsLines the totals engine's per-line answer (totals.lines: { index, net } in dollars) — revenue is the
 *             line's net (after any line discount, before tax), so the card agrees with the Total to the cent.
 *             Missing → quantity × price.
 * costs       { serviceId: number | null } from getCatalogCosts (ids not answered yet are "pending")
 * → { counted, noCost: [{ index, name }], pending, revenue, cost, profit, marginPct }
 *   marginPct is profit ÷ revenue × 100 to one decimal, or null when revenue is 0 or less.
 */
export function computeEstimateProfit({ lines, totalsLines, costs }) {
    const list = Array.isArray(lines) ? lines : [];
    const byIndex = new Map();
    for (const t of Array.isArray(totalsLines) ? totalsLines : []) if (t && Number.isInteger(t.index)) byIndex.set(t.index, t);
    const known = costs && typeof costs === 'object' ? costs : {};
    let revenue = 0; let cost = 0; let counted = 0; let pending = 0;
    const noCost = [];
    list.forEach((l, index) => {
        if (isBlankLine(l)) return;
        const id = typeof l.serviceId === 'string' ? l.serviceId.trim() : '';
        const name = (typeof l.name === 'string' && l.name.trim()) || 'Untitled line';
        if (!id) { noCost.push({ index, name }); return; }
        if (!Object.prototype.hasOwnProperty.call(known, id)) { pending += 1; return; }
        const unit = known[id];
        if (typeof unit !== 'number' || !Number.isFinite(unit)) { noCost.push({ index, name }); return; }
        const t = byIndex.get(index);
        revenue += t ? toCents(t.net) : toCents((Number(l.quantity) || 0) * (Number(l.price) || 0));
        cost += toCents((Number(l.quantity) || 0) * unit);
        counted += 1;
    });
    const profit = revenue - cost;
    return {
        counted,
        noCost,
        pending,
        revenue: revenue / 100,
        cost: cost / 100,
        profit: profit / 100,
        marginPct: revenue > 0 ? Math.round((profit / revenue) * 1000) / 10 : null,
    };
}

/** The yellow note's words: "2 lines have no cost, not counted: A, B." (names, at most 3 then "and N more"). */
export function noCostNote(noCost) {
    const n = Array.isArray(noCost) ? noCost.length : 0;
    if (!n) return '';
    const names = noCost.slice(0, 3).map((x) => x.name);
    const more = n > 3 ? ` and ${n - 3} more` : '';
    return `${n} ${n === 1 ? 'line has' : 'lines have'} no cost, not counted: ${names.join(', ')}${more}.`;
}
